/**
 *  @swagger
 *  definitions:
 *    RegattaEntryTrackPointParams:
 *      type: object
 *      properties:
 *        RegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        Timestamp:
 *          description: Timestamp of the RegattaEntryTrackPoint
 *          type: integer
 *        Lat:
 *          description: Lat of the RegattaEntryTrackPoint
 *          type: integer
 *        Lng:
 *          description: Lng of the RegattaEntryTrackPoint
 *          type: integer
 *        Heading:
 *          description: Heading of the RegattaEntryTrackPoint
 *          type: integer
 *        Speed:
 *          description: Speed of the RegattaEntryTrackPoint
 *          type: integer
 *        Battery:
 *          description: Battery of the RegattaEntryTrackPoint
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryTrackPointCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryTrackPointParams'
 *        - type: object
 *          required:
 *            - RegattaEntryId
 *            - Timestamp
 *            - Lat
 *            - Lng
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryTrackPointQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaEntryTrackPoint
 *              type: string
 *        - $ref: '#/definitions/RegattaEntryTrackPointParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryTrackPointUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryTrackPointQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryTrackPoint:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryTrackPointCreateParams'
 *        - $ref: '#/definitions/RegattaEntryTrackPointUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaEntryTrackPoint:
 *        allOf:
 *          - $ref: '#/definitions/RegattaEntryTrackPoint'
 *              RegattaEntry:
 *                description: RegattaEntry of the RegattaEntryTrackPoint
 *                $ref: '#/components/schemas/RegattaEntry'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaEntryId: string;
};

export type $Params = {
  Battery: null | number;
  Heading: null | number;
  Lat: number;
  Lng: number;
  Speed: null | number;
  Timestamp: number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaEntry?: $Model<'RegattaEntry'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Battery: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Heading: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Lat: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  Lng: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  Speed: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Timestamp: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaEntryTrackPoint'> => {
  const RegattaEntryTrackPoint = stack.define(
    'RegattaEntryTrackPoint',
    fields(Sequelize),
    {
      tableName: 'RegattaEntryTrackPoints',
    },
  );

  RegattaEntryTrackPoint.associate = (models: $ModelClasses) => {
    models.RegattaEntryTrackPoint.belongsTo(
      models.RegattaEntry,
      {
        as: 'RegattaEntry',
        foreignKey: 'RegattaEntryId',
      },
    );
  };

  RegattaEntryTrackPoint.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaEntryTrackPoint;
});
